import type { FC } from 'react';
import { useIntl } from 'react-intl';

import { Option } from '@xing-com/text-field';

import * as Styled from './pages-form-field.styles';

type ListType = {
  value: string;
  text: string;
}[];

type ValidationResult = null | {
  key: string;
  value?: number;
};

const getFieldCopy = (
  copyPrefix: string | undefined,
  type: string,
  translate: ReturnType<typeof useIntl>['$t']
): string => {
  const id = `${copyPrefix ?? ''}${type.toUpperCase()}`;
  switch (id) {
    case 'EP_EDIT_ABOUTUS_INTRO_TITLE':
    case 'EP_EDIT_ABOUTUS_INTRO_DESCRIPTION':
    case 'EP_EDIT_LOCATION_LABEL':
    case 'EP_EDIT_LOCATION_ADDRESS':
    case 'EP_EDIT_LOCATION_ADDRESSSUFFIX':
    case 'EP_EDIT_LOCATION_POSTCODE':
    case 'EP_EDIT_LOCATION_CITY':
    case 'EP_EDIT_LOCATION_COUNTRY':
    case 'EP_EDIT_LOCATION_PHONENUMBERCOUNTRYCODE':
    case 'EP_EDIT_LOCATION_PHONENUMBER':
    case 'EP_EDIT_LOCATION_FAXNUMBERCOUNTRYCODE':
    case 'EP_EDIT_LOCATION_FAXNUMBER':
    case 'EP_EDIT_LOCATION_EMAIL':
    case 'EP_EDIT_LOCATION_WEBSITEURL':
      return translate({ id, defaultMessage: id });
    default:
      return id;
  }
};

const getErrorMessage = (
  error: ValidationResult | undefined,
  translate: ReturnType<typeof useIntl>['$t']
) => {
  const translateError = (id: string, value?: number) =>
    translate({ id, defaultMessage: id }, value !== undefined ? { value } : {});

  if (!error?.key) {
    return null;
  }

  switch (error.key) {
    case 'EP_FIELD_EMPTY_OR_MIN_CHARACTERS_ERROR':
    case 'EP_FIELD_MAX_CHARACTERS_ERROR':
    case 'EP_LOCATION_LABEL_ERROR':
    case 'EP_LOCATION_ADDRESS_ERROR':
    case 'EP_LOCATION_EMAIL_ERROR':
    case 'EP_LOCATION_PHONENUMBER_ERROR':
    case 'EP_LOCATION_FAXNUMBER_ERROR':
    case 'EP_LOCATION_POSTCODE_ERROR':
    case 'EP_LOCATION_CITY_ERROR':
    case 'EP_LOCATION_WEBSITE_ERROR':
      return translateError(error.key, error.value);
    default:
      return error.key;
  }
};

type PagesFormFieldProps = {
  type: string;
  formState?: any;
  setFormTouched?: (isTouched: boolean) => void;
  setFormState?: (state: any) => void;
  optionList?: ListType;
  defaultValue?: string | number;
  applyToValue?: (value: string) => string;
  applyToValueOnFocus?: (value: string) => string;
  applyToValueOnBlur?: (value: string) => string;
  required?: boolean;
  validate?: (type: string, value: string) => ValidationResult;
  copyPrefix?: string;
  maxAllowedChars?: number;
  shouldTrim?: boolean;
  multiline?: boolean;
};
export const PagesFormField: FC<PagesFormFieldProps> = ({
  type,
  formState,
  setFormState = () => undefined,
  setFormTouched = () => undefined,
  optionList,
  defaultValue,
  applyToValue,
  applyToValueOnFocus,
  applyToValueOnBlur,
  required,
  validate,
  shouldTrim,
  copyPrefix,
  maxAllowedChars,
  ...props
}) => {
  const { $t } = useIntl();
  const fieldCopy = getFieldCopy(copyPrefix, type, $t);

  const changeHandler = (
    value: string,
    blurred?: boolean,
    focussed?: boolean
  ) => {
    const val =
      applyToValueOnBlur && blurred
        ? applyToValueOnBlur(value)
        : focussed && applyToValueOnFocus
          ? applyToValueOnFocus(value)
          : maxAllowedChars
            ? value.substring(0, maxAllowedChars)
            : value;
    const error = validate && validate(type, shouldTrim ? val.trim() : val);
    const newValue = shouldTrim && blurred ? val.trim() : val;

    setFormState({
      ...formState,
      [type]: { value: newValue, showError: !!blurred && !!error, error },
    });
    setFormTouched(true);
  };

  if (optionList) {
    return (
      <Styled.Select
        value={formState?.[type]?.value || defaultValue || ''}
        error={formState?.[type]?.error?.key}
        onChange={(e: any) => changeHandler && changeHandler(e.target.value)}
        placeholder={fieldCopy}
        // @ts-expect-error TS(2769) FIXME: No overload matches this call.
        medium
        {...props}
      >
        {optionList?.map((item, index) => (
          <Option key={index} value={item.value}>
            {item.text}
          </Option>
        ))}
      </Styled.Select>
    );
  }

  return (
    <Styled.FormField
      // @ts-expect-error TS(2769) FIXME: No overload matches this call.
      helperText={
        formState?.[type]?.showError && formState?.[type]?.error?.key
          ? getErrorMessage(formState[type].error, $t)
          : maxAllowedChars
            ? $t(
                {
                  id: 'EP_INPUT_CHARS_LEFT',
                  defaultMessage: 'EP_INPUT_CHARS_LEFT',
                },
                {
                  charactersNumber:
                    maxAllowedChars - formState?.[type]?.value?.length,
                }
              )
            : !required
              ? $t({
                  id: 'EP_OPTIONAL_FIELD_HINT',
                  defaultMessage: 'EP_OPTIONAL_FIELD_HINT',
                })
              : null
      }
      value={
        formState?.[type]?.value
          ? applyToValue
            ? applyToValue(formState?.[type]?.value)
            : formState?.[type]?.value
          : ''
      }
      error={formState?.[type]?.showError}
      onChange={(e: any) => changeHandler(e.target.value)}
      onBlur={(e: any) => changeHandler(e.target.value, true)}
      onFocus={(e: any) =>
        applyToValueOnFocus && changeHandler(e.target.value, false, true)
      }
      label={fieldCopy}
      medium
      {...props}
    />
  );
};
